import React, { useContext, useEffect, useState, useRef } from 'react'
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Grid,
    InputAdornment,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import {
    AccountBalanceWallet,
    AccessTime,
    Add,
    CalendarMonth,
    Cancel,
    FilterList,
    Search,
    WarningAmber,
} from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'
import { Helmet } from 'react-helmet'
import Context from '../../../contexts/Context'
import NoResultsAnimation from '../../../components/Shared/animations/NoResultsAnimation'
import PaginationGlobal from '../../../components/Shared/PaginationGlobal'
import Masks from '../../../components/Shared/Masks'
import { useNavigate } from 'react-router-dom'

const STATUS_OPTIONS = [
    { key: null, label: 'Todos', color: '#64748B' },
    { key: 'pending', label: 'Pendente', color: '#F59E0B' },
    { key: 'paid', label: 'Pago', color: '#22C55E' },
    { key: 'overdue', label: 'Vencido', color: '#EF4444' },
    { key: 'canceled', label: 'Cancelado', color: '#94A3B8' },
]

const getStatusChip = (statusKey) => {
    const found = STATUS_OPTIONS.find((s) => s.key === statusKey)
    if (!found) return null
    return (
        <Chip
            label={found.label}
            size="small"
            sx={{
                bgcolor: `${found.color}14`,
                color: found.color,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 26,
                border: `1px solid ${found.color}30`,
            }}
        />
    )
}

const PaymentsList = () => {
    const navigate = useNavigate()
    const { user } = useContext(Context)
    const typingTimerRef = useRef(null)

    const [response, setResponse] = useState(null)
    const [summary, setSummary] = useState(null)
    const [activeStatus, setActiveStatus] = useState(null)
    const [search, setSearch] = useState(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate())
    const [endDate, setEndDate] = useState(moment().endOf('month').toDate())
    const [filters, setFilters] = useState({
        condo_id: getCondoId(),
        page_size: 50,
        page: 1,
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
    })

    function getPayments() {
        const params = {
            date_from: filters.startDate,
            date_to: filters.endDate,
            page_size: filters.page_size,
            page: filters.page,
            condo_id: getCondoId(),
            ordering: filters.ordering || '-payment_date',
        }

        if (activeStatus) params.status = activeStatus
        if (search) params.search = search

        if (params.date_from && params.date_to) {
            api.get('cashflow/payments/', { params })
                .then((res) => setResponse(res.data))
                .catch((error) => console.log(error))
                .finally(() => setLoading(false))
        }
    }

    useEffect(() => {
        if (response) {
            const summaryParams = {
                condo_id: getCondoId(),
                date_from: filters.startDate,
                date_to: filters.endDate,
            }
            api.get('cashflow/payments/summary/', { params: summaryParams })
                .then((res) => setSummary(res.data))
                .catch(() => {})
        }
    }, [response])

    useEffect(() => {
        getPayments()
        window.scrollTo(0, 0)
    }, [filters, activeStatus, search])

    return (
        <>
            <Helmet>
                <title>Pagamentos - Bloco B3</title>
            </Helmet>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Pagamentos
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate('/financeiro/pagamentos/criar')}
                            sx={{
                                bgcolor: '#3B82F6',
                                '&:hover': { bgcolor: '#2563EB' },
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                            }}
                        >
                            Novo Pagamento
                        </Button>
                    </Box>

                    {/* KPI Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ bgcolor: '#22C55E14', borderRadius: 2, p: 1, display: 'flex' }}>
                                        <AccountBalanceWallet sx={{ color: '#22C55E', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            Total Pago
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                                            {Masks.money(String(Math.round((summary?.total_paid || 0) * 100)))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ bgcolor: '#F59E0B14', borderRadius: 2, p: 1, display: 'flex' }}>
                                        <AccessTime sx={{ color: '#F59E0B', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            Total Pendente
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                                            {Masks.money(String(Math.round((summary?.total_pending || 0) * 100)))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ bgcolor: '#EF444414', borderRadius: 2, p: 1, display: 'flex' }}>
                                        <WarningAmber sx={{ color: '#EF4444', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            Total Vencido
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                                            {Masks.money(String(Math.round((summary?.total_overdue || 0) * 100)))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ p: 2.5, border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ bgcolor: '#94A3B814', borderRadius: 2, p: 1, display: 'flex' }}>
                                        <Cancel sx={{ color: '#94A3B8', fontSize: 24 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                            Total Cancelado
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                                            {Masks.money(String(Math.round((summary?.total_canceled || 0) * 100)))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Filters Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            mb: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FilterList sx={{ color: '#94A3B8', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                                    Filtros
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por contato ou descrição..."
                                        variant="outlined"
                                        size="small"
                                        onKeyUp={(event) => {
                                            clearTimeout(typingTimerRef.current)
                                            typingTimerRef.current = setTimeout(() => setSearch(event.target.value), 800)
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                bgcolor: '#fff',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="De"
                                        value={startDate}
                                        onChange={(val) => {
                                            setStartDate(val)
                                            setFilters((prev) => ({
                                                ...prev,
                                                startDate: moment(val).format('YYYY-MM-DD'),
                                            }))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={2.5}>
                                    <DesktopDatePicker
                                        label="Até"
                                        value={endDate}
                                        onChange={(val) => {
                                            setEndDate(val)
                                            setFilters((prev) => ({
                                                ...prev,
                                                endDate: moment(val).format('YYYY-MM-DD'),
                                            }))
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                inputProps: { readOnly: true },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        bgcolor: '#fff',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Status Chips */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                {STATUS_OPTIONS.map((opt) => (
                                    <Chip
                                        key={opt.key ?? 'all'}
                                        label={opt.label}
                                        size="small"
                                        onClick={() => setActiveStatus(opt.key)}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            height: 32,
                                            px: 0.5,
                                            bgcolor: activeStatus === opt.key ? `${opt.color}14` : 'transparent',
                                            color: activeStatus === opt.key ? opt.color : '#64748B',
                                            border: activeStatus === opt.key ? `1.5px solid ${opt.color}` : '1.5px solid #E2E8F0',
                                            '&:hover': {
                                                bgcolor: `${opt.color}14`,
                                                borderColor: opt.color,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Card>

                    {/* Table Card */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        {response?.results ? (
                            <TableContainer>
                                {response?.results?.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Contato
                                                </TableCell>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Vencimento
                                                </TableCell>
                                                <TableCell sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Categoria
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Status
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#94A3B8', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #E2E8F0' }}>
                                                    Valor
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {response?.results?.map((item) => (
                                                <TableRow
                                                    key={item.id}
                                                    onClick={() => navigate(`/financeiro/pagamentos/${item.id}`)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.15s ease',
                                                        '&:hover': { bgcolor: '#F1F5F9' },
                                                        '& td': { borderBottom: '1px solid #F1F5F9' },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: '#1E293B', fontWeight: 500 }}>
                                                            {item.contact_detail?.name || item.description || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <CalendarMonth sx={{ fontSize: 16, color: '#CBD5E1' }} />
                                                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                                {item.due_date ? moment(item.due_date).format('DD/MM/YYYY') : '-'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                                                            {item.category_detail?.name || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getStatusChip(item.computed_status)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                            {Masks.money(String(Math.round((item.value || 0) * 100)))}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <NoResultsAnimation />
                                )}
                            </TableContainer>
                        ) : (
                            <Skeleton variant="rounded" height={500} animation="pulse" sx={{ m: 2 }} />
                        )}
                    </Card>

                    {response?.count > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PaginationGlobal state={response} filters={filters} setFilters={setFilters} />
                        </Box>
                    )}
                </Container>
            </Box>
        </>
    )
}

export default PaymentsList
